import React, { useState, useCallback } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Button,
    TextField,
    Typography,
    Box,
    Chip,
    Tooltip,
    IconButton,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Icon,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Switch,
    FormControlLabel
} from '@material-ui/core';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Edit as EditIcon,
    Apps as AppsIcon,
    OpenInNew as OpenInNewIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    root: {
        marginBottom: theme.spacing(2)
    },
    cardHeader: {
        paddingBottom: '8px',
        backgroundColor: '#f7f9fc'
    },
    addButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(2),
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        color: theme.palette.text.secondary,
        cursor: 'pointer',
        backgroundColor: 'transparent',
        width: '100%',
        minHeight: theme.spacing(8),
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
        },
    },
    applicationCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.2s ease',
        '&:hover': {
            boxShadow: theme.shadows[2],
        },
    },
    applicationHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1.5, 2),
        backgroundColor: '#f7f9fc',
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    applicationInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flex: 1,
    },
    applicationIcon: {
        width: 40,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
    },
    applicationDetails: {
        flex: 1,
    },
    applicationActions: {
        display: 'flex',
        gap: theme.spacing(0.5),
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(3),
        color: theme.palette.text.secondary,
    },
    categoryChip: {
        marginLeft: theme.spacing(1),
        height: 20,
        fontSize: '0.7rem',
    },
    urlPreview: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        fontFamily: 'monospace',
        maxWidth: 300,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    }
}));

// Available icon options for applications
const iconOptions = [
    { value: 'apps', label: 'Apps', icon: 'apps' },
    { value: 'code', label: 'Code', icon: 'code' },
    { value: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
    { value: 'terminal', label: 'Terminal', icon: 'terminal' },
    { value: 'analytics', label: 'Analytics', icon: 'analytics' },
    { value: 'memory', label: 'Memory/AI', icon: 'memory' },
    { value: 'assessment', label: 'Assessment', icon: 'assessment' },
    { value: 'science', label: 'Science', icon: 'science' },
    { value: 'computer', label: 'Computer', icon: 'computer' },
    { value: 'storage', label: 'Storage', icon: 'storage' },
    { value: 'cloud', label: 'Cloud', icon: 'cloud' },
    { value: 'dns', label: 'DNS/Network', icon: 'dns' },
    { value: 'layers', label: 'Layers', icon: 'layers' },
    { value: 'data_usage', label: 'Data', icon: 'data_usage' },
    { value: 'insert_chart', label: 'Charts', icon: 'insert_chart' },
    { value: 'school', label: 'Education', icon: 'school' },
    { value: 'settings', label: 'Settings', icon: 'settings' },
    { value: 'web', label: 'Web', icon: 'web' },
];

// Category options
const categoryOptions = [
    { value: 'general', label: 'General' },
    { value: 'monitoring', label: 'Monitoring' },
    { value: 'development', label: 'Development' },
    { value: 'ai', label: 'AI/ML' },
    { value: 'data', label: 'Data Science' },
    { value: 'admin', label: 'Administration' },
    { value: 'education', label: 'Education' },
    { value: 'other', label: 'Other' },
];

// Launch type options
const launchTypeOptions = [
    { value: 'url', label: 'Open URL', description: 'Opens a web URL in a new tab' },
    { value: 'instructions', label: 'Show Instructions', description: 'Shows instructions dialog with action button' },
];

// Action type options (for instructions launch type)
const actionTypeOptions = [
    { value: 'access-tab', label: 'Go to Access Tab', description: 'Navigate to cluster Access tab' },
    { value: 'remote-desktop', label: 'Open Remote Desktop', description: 'Launch VNC remote desktop directly' },
    { value: 'none', label: 'No Action', description: 'Just show instructions without action button' },
];

// Generate unique ID
const generateId = () => {
    return 'app-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
};

const ClusterApplicationsSection = ({ 
    applications = [], 
    onChange,
    disabled = false,
    cardStyle = {},
    cardHeaderStyle = {},
    sectionTitleStyle = {}
}) => {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingApp, setEditingApp] = useState(null);
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        url: '',
        icon: 'apps',
        description: '',
        category: 'general',
        instructions: '',
        enabled: true,
        launchType: 'url',        // 'url' or 'instructions'
        actionType: 'access-tab', // 'access-tab', 'remote-desktop', or 'none'
        actionButtonLabel: '',    // Custom label for action button
        ssoEnabled: false,        // Feature: sso-classifyx - Enable Heidi SSO token injection
        appId: ''                 // Feature: sso-classifyx - SSO app identifier (e.g., "classifyx")
    });
    const [errors, setErrors] = useState({});

    // Validate form
    const validateForm = useCallback(() => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        
        // Validation depends on launch type
        if (formData.launchType === 'url') {
            // URL is required for URL launch type
            if (!formData.url.trim()) {
                newErrors.url = 'URL is required';
            }
        } else if (formData.launchType === 'instructions') {
            // Instructions are required for instructions launch type
            if (!formData.instructions.trim()) {
                newErrors.instructions = 'Instructions are required for this launch type';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData]);

    // Handle opening the add/edit dialog
    const handleOpenDialog = (app = null) => {
        if (app) {
            setEditingApp(app);
            setFormData({
                id: app.id,
                name: app.name || '',
                url: app.url || '',
                icon: app.icon || 'apps',
                description: app.description || '',
                category: app.category || 'general',
                instructions: app.instructions || '',
                enabled: app.enabled !== false,
                launchType: app.launchType || 'url',
                actionType: app.actionType || 'access-tab',
                actionButtonLabel: app.actionButtonLabel || '',
                ssoEnabled: app.ssoEnabled || false,
                appId: app.appId || ''
            });
        } else {
            setEditingApp(null);
            setFormData({
                id: generateId(),
                name: '',
                url: '',
                icon: 'apps',
                description: '',
                category: 'general',
                instructions: '',
                enabled: true,
                launchType: 'url',
                actionType: 'access-tab',
                actionButtonLabel: '',
                ssoEnabled: false,
                appId: ''
            });
        }
        setErrors({});
        setDialogOpen(true);
    };

    // Handle closing the dialog
    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingApp(null);
        setErrors({});
    };

    // Handle saving an application
    const handleSaveApplication = () => {
        if (!validateForm()) {
            return;
        }

        const newApp = {
            id: formData.id,
            name: formData.name.trim(),
            url: formData.url.trim(),
            icon: formData.icon,
            description: formData.description.trim(),
            category: formData.category,
            instructions: formData.instructions.trim(),
            enabled: formData.enabled,
            launchType: formData.launchType,
            actionType: formData.actionType,
            actionButtonLabel: formData.actionButtonLabel.trim(),
            ssoEnabled: formData.ssoEnabled,
            appId: formData.appId.trim()
        };

        let updatedApps;
        if (editingApp) {
            // Update existing
            updatedApps = applications.map(app => 
                app.id === editingApp.id ? newApp : app
            );
        } else {
            // Add new
            updatedApps = [...applications, newApp];
        }

        onChange(updatedApps);
        handleCloseDialog();
    };

    // Handle deleting an application
    const handleDeleteApplication = (appId) => {
        const updatedApps = applications.filter(app => app.id !== appId);
        onChange(updatedApps);
    };

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user types
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    // Get category label
    const getCategoryLabel = (categoryValue) => {
        const cat = categoryOptions.find(c => c.value === categoryValue);
        return cat ? cat.label : categoryValue;
    };

    return (
        <Card style={cardStyle} className={classes.root}>
            <CardHeader
                title={
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Box display="flex" alignItems="center">
                            <Icon style={{ marginRight: '8px', color: '#2196F3' }}>apps</Icon>
                            <Typography style={sectionTitleStyle}>Cluster Applications</Typography>
                            {applications.length > 0 && (
                                <Chip 
                                    label={applications.length} 
                                    size="small" 
                                    color="primary" 
                                    style={{ marginLeft: 8, height: 20 }}
                                />
                            )}
                        </Box>
                        <IconButton 
                            size="small" 
                            onClick={() => setExpanded(!expanded)}
                        >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                }
                style={cardHeaderStyle}
                className={classes.cardHeader}
            />
            <Collapse in={expanded}>
                <CardContent>
                    {/* Description */}
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                        Add web applications and services that this cluster provides (e.g., JupyterLab, TensorBoard, WebUI). 
                        These will be accessible from the Applications tab after deployment.
                    </Typography>

                    {/* Applications list */}
                    {applications.length > 0 ? (
                        <Box mb={2}>
                            {applications.map((app, index) => (
                                <Box key={app.id} className={classes.applicationCard}>
                                    <Box className={classes.applicationHeader}>
                                        <Box className={classes.applicationInfo}>
                                            <Box className={classes.applicationIcon}>
                                                <Icon>{app.icon || 'apps'}</Icon>
                                            </Box>
                                            <Box className={classes.applicationDetails}>
                                                <Box display="flex" alignItems="center">
                                                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                                        {app.name}
                                                    </Typography>
                                                    <Chip
                                                        label={getCategoryLabel(app.category)}
                                                        size="small"
                                                        className={classes.categoryChip}
                                                    />
                                                    {app.launchType === 'instructions' && (
                                                        <Chip
                                                            label="Instructions"
                                                            size="small"
                                                            color="secondary"
                                                            className={classes.categoryChip}
                                                            icon={<Icon style={{ fontSize: 12 }}>info</Icon>}
                                                        />
                                                    )}
                                                    {app.ssoEnabled && (
                                                        <Chip
                                                            label="SSO"
                                                            size="small"
                                                            color="primary"
                                                            className={classes.categoryChip}
                                                            icon={<Icon style={{ fontSize: 12 }}>vpn_key</Icon>}
                                                        />
                                                    )}
                                                    {!app.enabled && (
                                                        <Chip
                                                            label="Disabled"
                                                            size="small"
                                                            color="default"
                                                            className={classes.categoryChip}
                                                        />
                                                    )}
                                                </Box>
                                                <Typography className={classes.urlPreview}>
                                                    {app.launchType === 'instructions' 
                                                        ? `Action: ${actionTypeOptions.find(a => a.value === app.actionType)?.label || 'Show Instructions'}`
                                                        : app.url
                                                    }
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box className={classes.applicationActions}>
                                            <Tooltip title="Edit">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleOpenDialog(app)}
                                                    disabled={disabled}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleDeleteApplication(app.id)}
                                                    disabled={disabled}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                    {app.description && (
                                        <Box p={1.5} pt={1}>
                                            <Typography variant="body2" color="textSecondary">
                                                {app.description}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Box className={classes.emptyState} mb={2}>
                            <AppsIcon style={{ fontSize: 48, marginBottom: 8, opacity: 0.5 }} />
                            <Typography variant="body2">
                                No applications defined yet. Click below to add one.
                            </Typography>
                        </Box>
                    )}

                    {/* Add button */}
                    <Button
                        className={classes.addButton}
                        onClick={() => handleOpenDialog()}
                        disabled={disabled}
                    >
                        <AddIcon style={{ marginRight: 8 }} />
                        Add Application
                    </Button>
                </CardContent>
            </Collapse>

            {/* Add/Edit Dialog */}
            <Dialog 
                open={dialogOpen} 
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <Icon style={{ marginRight: 8, color: '#2196F3' }}>
                            {editingApp ? 'edit' : 'add_circle'}
                        </Icon>
                        {editingApp ? 'Edit Application' : 'Add Application'}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        {/* Name */}
                        <Grid item xs={12} sm={8}>
                            <TextField
                                fullWidth
                                label="Application Name"
                                value={formData.name}
                                onChange={(e) => handleFieldChange('name', e.target.value)}
                                error={!!errors.name}
                                helperText={errors.name || 'e.g., JupyterLab, TensorBoard, WebUI'}
                                margin="dense"
                                variant="outlined"
                                required
                            />
                        </Grid>

                        {/* Icon */}
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth margin="dense" variant="outlined">
                                <InputLabel>Icon</InputLabel>
                                <Select
                                    value={formData.icon}
                                    onChange={(e) => handleFieldChange('icon', e.target.value)}
                                    label="Icon"
                                >
                                    {iconOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            <Box display="flex" alignItems="center">
                                                <Icon style={{ marginRight: 8, fontSize: 20 }}>{opt.icon}</Icon>
                                                {opt.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Launch Type */}
                        <Grid item xs={12}>
                            <FormControl fullWidth margin="dense" variant="outlined">
                                <InputLabel>Launch Type</InputLabel>
                                <Select
                                    value={formData.launchType}
                                    onChange={(e) => handleFieldChange('launchType', e.target.value)}
                                    label="Launch Type"
                                >
                                    {launchTypeOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            <Box>
                                                <Typography variant="body2">{opt.label}</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {opt.description}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    {formData.launchType === 'url' 
                                        ? 'Opens a web URL when clicked'
                                        : 'Shows an instructions dialog with an action button'
                                    }
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        {/* URL Section - Only shown for URL launch type */}
                        {formData.launchType === 'url' && (
                            <>
                                <Grid item xs={12}>
                                    <Box mb={1} p={1.5} bgcolor="#e3f2fd" borderRadius={4}>
                                        <Typography variant="caption" color="textSecondary">
                                            <strong>💡 Tip:</strong> Use <code style={{ backgroundColor: '#fff', padding: '2px 4px', borderRadius: 2 }}>&lt;server-ip&gt;</code> as a placeholder - it will be replaced with the cluster's IP after deployment.
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="URL"
                                        value={formData.url}
                                        onChange={(e) => handleFieldChange('url', e.target.value)}
                                        error={!!errors.url}
                                        helperText={errors.url || 'e.g., http://<server-ip>:8888/lab'}
                                        margin="dense"
                                        variant="outlined"
                                        placeholder="http://<server-ip>:8888"
                                        required
                                        InputProps={{
                                            endAdornment: (
                                                <Tooltip title="Insert http://<server-ip>: template">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => {
                                                            if (!formData.url) {
                                                                handleFieldChange('url', 'http://<server-ip>:');
                                                            } else if (!formData.url.includes('<server-ip>')) {
                                                                handleFieldChange('url', 'http://<server-ip>:' + formData.url.replace(/^https?:\/\//, ''));
                                                            }
                                                        }}
                                                    >
                                                        <Icon fontSize="small">add_location</Icon>
                                                    </IconButton>
                                                </Tooltip>
                                            )
                                        }}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Instructions Section - Only shown for Instructions launch type */}
                        {formData.launchType === 'instructions' && (
                            <>
                                <Grid item xs={12}>
                                    <Box mb={1} p={1.5} bgcolor="#fff3e0" borderRadius={4}>
                                        <Typography variant="caption" color="textSecondary">
                                            <strong>📋 Instructions Mode:</strong> When users click this application, they will see a dialog with your instructions and an action button. 
                                            This is ideal for applications that need to be run inside the VNC desktop session (e.g., E4S, terminal commands).
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* Action Type */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth margin="dense" variant="outlined">
                                        <InputLabel>Action Button</InputLabel>
                                        <Select
                                            value={formData.actionType}
                                            onChange={(e) => handleFieldChange('actionType', e.target.value)}
                                            label="Action Button"
                                        >
                                            {actionTypeOptions.map((opt) => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    <Box>
                                                        <Typography variant="body2">{opt.label}</Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {actionTypeOptions.find(a => a.value === formData.actionType)?.description}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                {/* Custom Action Button Label */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Action Button Label (optional)"
                                        value={formData.actionButtonLabel}
                                        onChange={(e) => handleFieldChange('actionButtonLabel', e.target.value)}
                                        helperText={`Default: "${formData.actionType === 'remote-desktop' ? 'Open Remote Desktop' : formData.actionType === 'access-tab' ? 'Go to Access Tab' : 'Close'}"`}
                                        margin="dense"
                                        variant="outlined"
                                        placeholder="Custom button text"
                                    />
                                </Grid>

                                {/* Instructions - Required for this mode */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Instructions (HTML supported)"
                                        value={formData.instructions}
                                        onChange={(e) => handleFieldChange('instructions', e.target.value)}
                                        error={!!errors.instructions}
                                        helperText={errors.instructions || 'Enter detailed instructions. HTML tags like <b>, <code>, <ul>, <li> are supported.'}
                                        margin="dense"
                                        variant="outlined"
                                        multiline
                                        rows={6}
                                        required
                                        placeholder="<h4>How to access E4S</h4>\n<ol>\n  <li>Open a terminal in the remote desktop</li>\n  <li>Run: <code>e4s-cl init</code></li>\n  <li>Follow the prompts...</li>\n</ol>"
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Category */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth margin="dense" variant="outlined">
                                <InputLabel>Category</InputLabel>
                                <Select
                                    value={formData.category}
                                    onChange={(e) => handleFieldChange('category', e.target.value)}
                                    label="Category"
                                >
                                    {categoryOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* SSO Authentication - Feature: sso-classifyx */}
                        {formData.launchType === 'url' && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.ssoEnabled}
                                                onChange={(e) => handleFieldChange('ssoEnabled', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Enable SSO Authentication"
                                    />
                                    <FormHelperText style={{ marginTop: -4 }}>
                                        Injects a Heidi SSO token into the URL on launch
                                    </FormHelperText>
                                </Grid>
                                {formData.ssoEnabled && (
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="SSO App ID"
                                            value={formData.appId}
                                            onChange={(e) => handleFieldChange('appId', e.target.value)}
                                            helperText='Identifier for token generation (e.g., "classifyx")'
                                            margin="dense"
                                            variant="outlined"
                                            placeholder="classifyx"
                                            required
                                        />
                                    </Grid>
                                )}
                            </>
                        )}

                        {/* Description */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={formData.description}
                                onChange={(e) => handleFieldChange('description', e.target.value)}
                                helperText="Brief description of the application"
                                margin="dense"
                                variant="outlined"
                                multiline
                                rows={2}
                            />
                        </Grid>

                        {/* Instructions - Only for URL launch type (optional) */}
                        {formData.launchType === 'url' && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Pre-Launch Instructions (optional, HTML supported)"
                                    value={formData.instructions}
                                    onChange={(e) => handleFieldChange('instructions', e.target.value)}
                                    helperText="If provided, users will see these instructions before launching the application. HTML tags like <b>, <code>, <ul>, <li> are supported."
                                    margin="dense"
                                    variant="outlined"
                                    multiline
                                    rows={4}
                                    placeholder="<h4>Getting Started</h4>\n<p>Login with your HCC credentials. Default port is 8080.</p>"
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSaveApplication} 
                        color="primary" 
                        variant="contained"
                    >
                        {editingApp ? 'Save Changes' : 'Add Application'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default ClusterApplicationsSection;
